// =============================================================================
// payments — admin transactions feed wrapper
// =============================================================================
import { supabase } from './supabase';

// 'gift_card' = a physical gift card load on the WisePOS (booking.gift_card_loads,
// not a booking.payments row); listed under Stripe too.
export type PaymentMethod = 'stripe' | 'cash' | 'card_terminal' | 'gift_card';

export interface PaymentFeedRow {
  payment_id: string;
  // Null on a counter sale: a payment with a venue but no booking (the CCV
  // terminal taps, payment_method card_terminal + payment_method_type 'ccv').
  booking_id: string | null;
  booking_number: string | null;
  amount_cents: number;
  currency: string;
  status: string;                       // raw payment status
  payment_method: PaymentMethod | null;
  payment_method_type: string | null;   // ideal/card/etc (Stripe), 'ccv' (imported terminal tap)
  // Venue: the payment's own location_id, else the booking's first slot.
  // Absent on payloads from an older fn_admin_list_payments.
  location_id?: string | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
  updated_at: string;
  // Name typed at booking time; prefer over first/last. Null on older
  // rows and on payloads from an older fn_admin_list_payments.
  guest_name?: string | null;
  first_name: string | null;
  last_name: string | null;
  customer_email: string | null;   // null for anonymous counter walk-ins
  experience_name: string | null;
  first_slot_start: string | null;
  discount: { code: string; amount_off_cents: number } | null;
  // 'gift_card_load' rows carry the card instead of a booking. Absent on
  // payloads from an older fn_admin_list_payments.
  kind?: 'payment' | 'gift_card_load';
  gift_card?: {
    code: string;
    code_display: string;
    serial: number;
    is_activation: boolean;
    refunded_cents: number;
    operator_email: string | null;
  } | null;
}

export interface PaymentFeedResult {
  rows: PaymentFeedRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface PaymentFeedFilter {
  fromDate?: string | null;        // 'YYYY-MM-DD'
  toDate?: string | null;
  paymentMethod?: PaymentMethod | null;
  status?: string | null;
  search?: string | null;
  locationId?: string | null;
  limit?: number;
  offset?: number;
}

export async function listPayments(
  filter: PaymentFeedFilter = {},
): Promise<PaymentFeedResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_list_payments', {
      p_from:           filter.fromDate ?? null,
      p_to:             filter.toDate ?? null,
      p_payment_method: filter.paymentMethod ?? null,
      p_status:         filter.status ?? null,
      p_search:         filter.search && filter.search.trim().length > 0
                          ? filter.search.trim()
                          : null,
      p_limit:          filter.limit ?? 100,
      p_offset:         filter.offset ?? 0,
      p_location_id:    filter.locationId ?? null,
    });
  if (error) throw error;
  return (data as PaymentFeedResult) ?? { rows: [], total: 0, limit: 100, offset: 0 };
}

// Returns the Stripe live or test dashboard URL for a PaymentIntent.
// We don't know mode from the client, so default to a /test/ prefix when
// the publishable key (if surfaced) starts with pk_test_; otherwise hit live.
// The current widget hard-codes this from env, but the admin doesn't ship a
// publishable key. Fall back to the universal dashboard URL — Stripe redirects
// to the right mode for users logged into their dashboard.
export function stripePaymentIntentUrl(piId: string): string {
  return `https://dashboard.stripe.com/payments/${encodeURIComponent(piId)}`;
}
